import { CurrentTimeInfo, WorkerLogtimeData } from "@lhd/types";
import { useMemo } from "react";
import { FaWalking } from "react-icons/fa";
import { LOG_STATUS, LOG_TYPE } from "../contants";
import {
  calculateTotalWorkingMs,
  distributeTimelinePercents,
  formatDisplayMinutes,
  formatDuration,
  getDisplayMinutesAt,
  getEdgeAlignedStyle,
  getWorkdayEndDisplayMinutes,
  parseDisplayTimeMinutes,
  parseLoggedAtMs,
  sortLogsByLoggedAt,
} from "../until";
import { Styles } from "./styled";

type CurrentTimeQueryData = {
  data?: CurrentTimeInfo;
  datetimeWithTimeRes?: Date;
};

interface IProps {
  className?: string;
  workerLogtimesData?: WorkerLogtimeData;
  currentTimeData?: CurrentTimeQueryData;
  serverNowMs?: number;
}

const MARKER_LABEL: Partial<Record<LOG_TYPE, string>> = {
  [LOG_TYPE.CHECKIN]: "出勤",
  [LOG_TYPE.BREAK_START]: "休憩開始",
  [LOG_TYPE.BREAK_END]: "休憩終了",
  [LOG_TYPE.CHECKOUT]: "退勤",
};

const MIN_MARKER_GAP_PERCENT = 6;

const CurrentStatusTracking = (props: IProps) => {
  const { className, workerLogtimesData, serverNowMs } = props;
  const todayLogs = useMemo(() => {
    const logs = workerLogtimesData?.today_logs || [];
    return sortLogsByLoggedAt(logs);
  }, [workerLogtimesData?.today_logs]);

  const namedMarkers = useMemo(() => {
    return todayLogs
      .map((log) => {
        return {
          time: parseLoggedAtMs(log.logged_at),
          label: MARKER_LABEL[log.log_type as LOG_TYPE],
          logType: log.log_type,
          displayTime: log.display_time,
          displayMinutes: parseDisplayTimeMinutes(log.display_time),
        };
      })
      .filter(
        (
          marker,
        ): marker is {
          time: number;
          label: string;
          logType: LOG_TYPE;
          displayTime: string;
          displayMinutes: number;
        } =>
          !!marker.label &&
          marker.time > 0 &&
          typeof marker.displayTime === "string" &&
          marker.displayMinutes != null,
      );
  }, [todayLogs]);

  const nowMs = serverNowMs ?? Date.now();
  const checkInDisplayMinutes = namedMarkers[0]?.displayMinutes;
  const lastLog = todayLogs.at(-1);
  const isCheckedOut = lastLog?.log_type === LOG_TYPE.CHECKOUT;
  const workingMs = calculateTotalWorkingMs(
    todayLogs,
    isCheckedOut ? undefined : nowMs,
  );
  const last = namedMarkers.at(-1);

  const workdayEndDisplayMinutes = getWorkdayEndDisplayMinutes(
    workerLogtimesData?.day_change_time,
  );
  const domainEndDisplayMinutes =
    isCheckedOut && last
      ? last.displayMinutes
      : Math.max(workdayEndDisplayMinutes, (checkInDisplayMinutes ?? 0) + 1);
  const domainSpan = Math.max(
    domainEndDisplayMinutes - (checkInDisplayMinutes ?? 0),
    1,
  );
  const toPercent = (displayMinutes: number) =>
    Math.min(
      100,
      Math.max(
        0,
        ((displayMinutes - (checkInDisplayMinutes ?? 0)) / domainSpan) * 100,
      ),
    );
  const nowDisplayMinutes = last
    ? Math.max(
        last.displayMinutes,
        getDisplayMinutesAt(nowMs, last.time, last.displayMinutes),
      )
    : checkInDisplayMinutes ?? 0;
  const isFrozenAtLastMarker = last?.logType === LOG_TYPE.CHECKOUT;
  const isLastBreakStart = last?.logType === LOG_TYPE.BREAK_START;
  const showWalking =
    (!isFrozenAtLastMarker || !isCheckedOut) && !isLastBreakStart;
  const hasLivePosition = !isCheckedOut && last != null;
  const rawPointPercents = namedMarkers.map((marker) =>
    toPercent(marker.displayMinutes),
  );
  if (hasLivePosition) {
    rawPointPercents.push(toPercent(nowDisplayMinutes));
  }
  const pointPercents = distributeTimelinePercents(
    rawPointPercents,
    MIN_MARKER_GAP_PERCENT,
  );
  const markerPercents = pointPercents.slice(0, namedMarkers.length);
  const lastMarkerPercent = markerPercents.at(-1) ?? 0;
  const nowPercent = hasLivePosition
    ? pointPercents[namedMarkers.length] ?? lastMarkerPercent
    : lastMarkerPercent;
  const elapsedPercent = nowPercent;

  const walkingPercent = isFrozenAtLastMarker ? 100 : nowPercent;

  const segments: { start: number; end: number; type: LOG_STATUS }[] = [];

  for (let i = 0; i < namedMarkers.length - 1; i++) {
    const current = namedMarkers[i];

    if (
      namedMarkers?.length > 1 &&
      current.logType === LOG_TYPE.CHECKOUT &&
      namedMarkers[i + 1].logType === LOG_TYPE.CHECKIN
    ) {
      segments.push({
        start: markerPercents[i],
        end: markerPercents[i + 1],
        type: LOG_STATUS.BREAK,
      });
      continue;
    }

    segments.push({
      start: markerPercents[i],
      end: markerPercents[i + 1],
      type:
        current.logType === LOG_TYPE.BREAK_START
          ? LOG_STATUS.BREAK
          : LOG_STATUS.WORK,
    });
  }

  if (last) {
    if (last.logType !== LOG_TYPE.CHECKOUT) {
      segments.push({
        start: lastMarkerPercent,
        end: elapsedPercent,
        type:
          last.logType === LOG_TYPE.BREAK_START
            ? LOG_STATUS.BREAK
            : LOG_STATUS.WORK,
      });
    } else {
      segments.push({
        start: lastMarkerPercent,
        end: 100,
        type: LOG_STATUS.END,
      });
    }
  }

  return (
    <Styles className={className || ""}>
      <div className="status-title">
        本日の打刻状況（勤務時間
        {!namedMarkers.length
          ? "0時間00分（0.00H）"
          : formatDuration(workingMs)}
        ）
      </div>
      {!namedMarkers.length ? (
        <div className="timeline">
          <div className="timeline-track">
            <div className="timeline-dot left-0-percentage" />
            <div className="time-line-dot-start">出勤</div>
            <div className="timeline-dot right-0-percentage" />
            <div className="time-line-dot-end">退勤</div>
          </div>
        </div>
      ) : (
        <div className="timeline">
          <div className="timeline-track">
            {segments.map((segment, index) => (
              <div
                key={index}
                className={
                  segment.type === "break"
                    ? "timeline-break"
                    : "timeline-elapsed"
                }
                style={{
                  left: `${segment.start}%`,
                  width: `${Math.max(segment.end - segment.start, 0)}%`,
                }}
              />
            ))}
            {namedMarkers.map((_marker, index) => (
              <div
                key={index}
                className="timeline-dot"
                style={{ left: `${markerPercents[index]}%` }}
              />
            ))}
            {!isCheckedOut && !isFrozenAtLastMarker && !isLastBreakStart && (
              <div
                className="timeline-dot"
                style={{ left: `${nowPercent}%` }}
              />
            )}
            <div className="timeline-dot right-0-percentage" />
          </div>

          {namedMarkers.map((marker, index) => (
            <div
              key={index}
              className="timeline-label"
              style={getEdgeAlignedStyle(markerPercents[index])}
            >
              {marker.label}
            </div>
          ))}
          {showWalking && (
            <div
              className="timeline-now test-11"
              style={getEdgeAlignedStyle(walkingPercent)}
            >
              <FaWalking fontSize={26} />
            </div>
          )}

          {namedMarkers.map((marker, index) => (
            <div
              key={`time-${index}`}
              className="timeline-time"
              style={getEdgeAlignedStyle(markerPercents[index])}
            >
              {marker.displayTime}
            </div>
          ))}
          {showWalking && (
            <div
              className="timeline-time  test-12"
              style={getEdgeAlignedStyle(walkingPercent)}
            >
              {isFrozenAtLastMarker && last
                ? last.displayTime
                : formatDisplayMinutes(nowDisplayMinutes)}
            </div>
          )}
        </div>
      )}
    </Styles>
  );
};

export default CurrentStatusTracking;
